// src/lib/pwa.ts
// App instalable (PWA) con la marca de cada org.
//
// index.html trae un manifest base de Orion (public/manifest.webmanifest). En
// cuanto llega el branding de la org (BrandingContext), aplicarMarcaPwa()
// genera un manifest propio — nombre de la org e íconos dibujados desde su
// logo — y actualiza apple-touch-icon / apple-mobile-web-app-title, que es lo
// que lee Safari al «Agregar a pantalla de inicio». Así OnePercent se instala
// como OnePercent con su logo, Brainstream como Brainstream, etc.

export interface MarcaPwa {
  nombre: string;
  logo: string | null;
  fondo: string;
  tema: string;
  /** Ícono cuadrado propio de la org (dashboard.brand.app_icon); tiene prioridad sobre el logo. */
  icono?: string | null;
  /** Nombre bajo el ícono (dashboard.brand.app_short_name); sin él, el nombre recortado a 12. */
  nombreCorto?: string | null;
}

let manifestUrlActual: string | null = null;

function upsertLink(rel: string, href: string) {
  let link = document.querySelector<HTMLLinkElement>(`link[rel='${rel}']`);
  if (!link) {
    link = document.createElement('link');
    link.rel = rel;
    document.head.appendChild(link);
  }
  link.href = href;
}

function upsertMeta(name: string, content: string) {
  let meta = document.querySelector<HTMLMetaElement>(`meta[name='${name}']`);
  if (!meta) {
    meta = document.createElement('meta');
    meta.name = name;
    document.head.appendChild(meta);
  }
  meta.content = content;
}

function cargarImagen(src: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error('logo no cargó'));
    img.src = src;
  });
}

// Ícono cuadrado: fondo sólido + logo centrado al 72% (zona segura de los
// íconos "maskable" de Android). Devuelve null si el canvas queda "tainted"
// por CORS — en ese caso se conservan los íconos base de Orion.
function dibujarIcono(img: HTMLImageElement, size: number, fondo: string): string | null {
  const canvas = document.createElement('canvas');
  canvas.width = size;
  canvas.height = size;
  const ctx = canvas.getContext('2d');
  if (!ctx) return null;
  ctx.fillStyle = fondo;
  ctx.fillRect(0, 0, size, size);
  const caja = size * 0.72;
  const escala = Math.min(caja / img.width, caja / img.height);
  const w = img.width * escala;
  const h = img.height * escala;
  ctx.drawImage(img, (size - w) / 2, (size - h) / 2, w, h);
  try {
    return canvas.toDataURL('image/png');
  } catch {
    return null;
  }
}

// Cada llamada se numera: si llega otra mientras esta espera su imagen, esta se descarta
// (el logo remoto tarda más que el ícono propio y lo pisaría).
let llamadaActual = 0;

async function iconosDesde(src: string, fondo: string) {
  const img = await cargarImagen(src);
  const i192 = dibujarIcono(img, 192, fondo);
  const i512 = dibujarIcono(img, 512, fondo);
  const i180 = dibujarIcono(img, 180, fondo);
  return i192 && i512 && i180 ? { i192, i512, i180 } : null;
}

export async function aplicarMarcaPwa({ nombre, logo, fondo, tema, icono, nombreCorto }: MarcaPwa) {
  const yo = ++llamadaActual;
  const origen = window.location.origin;
  let iconos = [
    { src: `${origen}/icons/icon-192.png`, sizes: '192x192', type: 'image/png', purpose: 'any' },
    { src: `${origen}/icons/icon-512.png`, sizes: '512x512', type: 'image/png', purpose: 'any' },
    { src: `${origen}/icons/icon-512.png`, sizes: '512x512', type: 'image/png', purpose: 'maskable' },
  ];
  let appleIcon = `${origen}/icons/apple-touch-icon.png`;

  // Ícono propio de la org primero; si no carga o queda «tainted», el logo; si no, los de Orion.
  for (const fuente of [icono, logo]) {
    if (!fuente) continue;
    try {
      const r = await iconosDesde(fuente, fondo);
      if (r) {
        iconos = [
          { src: r.i192, sizes: '192x192', type: 'image/png', purpose: 'any' },
          { src: r.i512, sizes: '512x512', type: 'image/png', purpose: 'any' },
          { src: r.i512, sizes: '512x512', type: 'image/png', purpose: 'maskable' },
        ];
        appleIcon = r.i180;
        break;
      }
    } catch {
      // inaccesible → siguiente fuente
    }
  }
  if (yo !== llamadaActual) return; // llegó una marca más nueva mientras cargaba la imagen

  // El manifest vive en un blob: las URLs deben ser absolutas porque no hay
  // una base http contra la cual resolver rutas relativas.
  const manifest = {
    id: `${origen}/`,
    name: nombre,
    short_name: nombreCorto || (nombre.length > 12 ? nombre.slice(0, 12).trim() : nombre),
    lang: 'es',
    start_url: `${origen}/`,
    scope: `${origen}/`,
    display: 'standalone',
    background_color: fondo,
    theme_color: tema,
    icons: iconos,
  };
  const url = URL.createObjectURL(new Blob([JSON.stringify(manifest)], { type: 'application/manifest+json' }));
  upsertLink('manifest', url);
  if (manifestUrlActual) URL.revokeObjectURL(manifestUrlActual);
  manifestUrlActual = url;

  upsertLink('apple-touch-icon', appleIcon);
  upsertMeta('apple-mobile-web-app-title', nombreCorto || nombre);
  upsertMeta('application-name', nombre);
  upsertMeta('theme-color', tema);
}

// Solo en producción: en `vite dev` un SW estorba al HMR.
export function registrarServiceWorker() {
  if (!import.meta.env.PROD || !('serviceWorker' in navigator)) return;
  window.addEventListener('load', () => {
    navigator.serviceWorker.register('/sw.js', { scope: '/' }).catch(() => {});
  });
}

export function esStandalone(): boolean {
  return (
    window.matchMedia?.('(display-mode: standalone)').matches ||
    (navigator as Navigator & { standalone?: boolean }).standalone === true
  );
}

export function esIosSafari(): boolean {
  const ua = navigator.userAgent;
  const ios = /iPad|iPhone|iPod/.test(ua) || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
  return ios && /Safari/.test(ua) && !/CriOS|FxiOS|EdgiOS/.test(ua);
}

/** ¿El dashboard corre INSTALADO como app (pantalla de inicio en iPhone o
 *  Android, o app de escritorio)? Marca `<html data-app="instalada">` y lo
 *  mantiene al día. Con esa marca, index.css reserva las zonas seguras (hora,
 *  notch, barra de gestos) solo donde hace falta; en el navegador no cambia
 *  nada. iOS antiguo: `navigator.standalone`. */
export function marcarModoApp(): void {
  if (typeof window === 'undefined') return;
  const consultas = ['(display-mode: standalone)', '(display-mode: fullscreen)', '(display-mode: minimal-ui)']
    .map((q) => window.matchMedia?.(q))
    .filter((m): m is MediaQueryList => !!m);
  const aplicar = () => {
    const ios = (navigator as Navigator & { standalone?: boolean }).standalone === true;
    const instalada = ios || consultas.some((m) => m.matches);
    if (instalada) document.documentElement.dataset.app = 'instalada';
    else delete document.documentElement.dataset.app;
  };
  aplicar();
  consultas.forEach((m) => m.addEventListener?.('change', aplicar));
}
